'use client';

import { useId, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { tagOptionsAction } from '@/app/actions';
import { completeHashtag, hashtagAt, type TagOption } from '@/domain/item-tags';
import { Highlight, useAnchored } from '@/components/ui';
import { useT } from '@/lib/locale-context';
import { tagAdoptionMessage, tagPickerRows } from './tag-picker-rows';
import styles from './hashtag-autocomplete.module.css';

const MAX_ROWS = 6;

/**
 * The inline `#` autocomplete (docs/FIELDS_AND_TAGS.md §6.3: "typing `#`
 * opens the picker as autocomplete"). A hook over a text field the host owns:
 * it watches the `#token` the caret sits in, offers the viewer's ranked
 * vocabulary for it (the vote law, fetched once on the first `#`), and
 * completes the token on Enter or Tab. Focus never leaves the field - a
 * combobox over an anchored listbox (`aria-activedescendant`), the pattern the
 * Add bar and the criteria bar already use, at every width: a Sheet or a
 * `role="menu"` would take the caret away from the text being typed.
 *
 * Wire-up: the host puts `inputRef` on its field, routes `onChange`, `onSelect`
 * and `onKeyDown` through the hook's (its own keydown runs only when the hook
 * returns false), and renders `<HashtagSuggest suggest={ac.suggest}
 * floatingRef={ac.floatingRef} />`.
 */
export function useHashtagAutocomplete({
  value,
  setValue,
}: {
  value: string;
  setValue: (next: string) => void;
}) {
  const [caret, setCaret] = useState<number | null>(null);
  const [options, setOptions] = useState<TagOption[] | null>(null);
  const [active, setActive] = useState(0);
  const [dismissed, setDismissed] = useState<number | null>(null);
  const loading = useRef(false);
  const token = caret === null ? null : hashtagAt(value, caret);
  const rows =
    token && dismissed !== token.start
      ? tagPickerRows(token.query, options ?? [], new Set(), MAX_ROWS).filter(
          (r) => r.kind === 'tag',
        )
      : [];
  const open = rows.length > 0;
  const activeIndex = open ? Math.min(active, rows.length - 1) : -1;
  const { anchorRef: inputRef, floatingRef } = useAnchored<HTMLInputElement, HTMLDivElement>(open);
  const listId = useId();

  const load = () => {
    if (options !== null || loading.current) return;
    loading.current = true;
    tagOptionsAction().then(setOptions, () => setOptions([]));
  };
  const track = (text: string, at: number | null) => {
    setCaret(at);
    setActive(0);
    if (at !== null && hashtagAt(text, at)) load();
  };

  const complete = (i: number) => {
    const row = rows[i];
    if (!token || !row || row.kind !== 'tag') return;
    const next = completeHashtag(value, token, row.option.tag);
    setValue(next.text);
    setCaret(next.caret);
    requestAnimationFrame(() => inputRef.current?.setSelectionRange(next.caret, next.caret));
  };

  return {
    inputRef,
    floatingRef,
    /** What the list renders (`<HashtagSuggest suggest={...} />`). */
    suggest: { listId, open, rows, activeIndex, complete, setActive },
    /** The field's combobox wiring, present only while the list is open: at
     *  rest the host's field keeps its own role (a plain textbox). */
    fieldProps: open
      ? {
          role: 'combobox' as const,
          'aria-autocomplete': 'list' as const,
          'aria-expanded': true,
          'aria-controls': listId,
          'aria-activedescendant': `${listId}-${activeIndex}`,
        }
      : {},
    onChange(e: ChangeEvent<HTMLInputElement>) {
      setValue(e.target.value);
      setDismissed(null);
      track(e.target.value, e.target.selectionStart);
    },
    onSelect(e: { currentTarget: HTMLInputElement }) {
      if (e.currentTarget.selectionStart !== caret) {
        track(e.currentTarget.value, e.currentTarget.selectionStart);
      }
    },
    /** Handles the keys the open list owns; returns true when it did. */
    onKeyDown(e: KeyboardEvent<HTMLInputElement>): boolean {
      if (!open || !token) return false;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActive(Math.min(activeIndex + 1, rows.length - 1));
          return true;
        case 'ArrowUp':
          e.preventDefault();
          setActive(Math.max(activeIndex - 1, 0));
          return true;
        case 'Enter':
        case 'Tab':
          e.preventDefault();
          complete(activeIndex);
          return true;
        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          setDismissed(token.start);
          return true;
        default:
          return false;
      }
    },
  };
}

/** The anchored listbox under the field (render only through the hook). */
export function HashtagSuggest({
  suggest: { open, rows, activeIndex, listId, complete, setActive },
  floatingRef,
}: {
  suggest: ReturnType<typeof useHashtagAutocomplete>['suggest'];
  floatingRef: ReturnType<typeof useHashtagAutocomplete>['floatingRef'];
}) {
  const t = useT();
  if (!open) return null;
  return (
    <div
      ref={floatingRef}
      id={listId}
      role="listbox"
      aria-label={t('checklist.hashtag.list')}
      className={styles.list}
      data-testid="hashtag-suggest"
    >
      {rows.map((row, i) =>
        row.kind === 'tag' ? (
          <div
            key={row.option.fold}
            id={`${listId}-${i}`}
            role="option"
            aria-selected={i === activeIndex}
            className={styles.option}
            data-active={i === activeIndex || undefined}
            onMouseMove={() => setActive(i)}
            // Keep the field's focus (and the on-screen keyboard) on a tap.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => complete(i)}
          >
            <span className={styles.path}>
              #
              <Highlight
                text={row.option.tag}
                positions={row.positions}
                markClassName={styles.mark}
              />
            </span>
            <span className={styles.count}>{t.msg(tagAdoptionMessage(row.option))}</span>
          </div>
        ) : null,
      )}
    </div>
  );
}
